import { isUndefined } from 'lodash'
import { useCallback, useEffect } from 'react'
import { useCookies } from 'react-cookie'
import {
    useGetUserListApiV1WebpushNotificationsListGet,
    useSubscribeApiV1WebpushNotificationsSubscribePost,
} from '../../api/gen/base'
import { useUserStore } from '../../store/storeUser'
import { useWrapperStore } from '../../store/storeWrapper'
import { isBrowserSupportedNotifications } from '../../utils/notifications'
import { useWebPushNotificationsContext } from './WebPushNotificationsContext'

type UseWebPushNotificationsParams = {
    onPermissionDenied: VoidFunction
    onRequestPermission: VoidFunction
}

export const useWebPushNotifications = ({
    onPermissionDenied,
    onRequestPermission,
}: UseWebPushNotificationsParams) => {
    const { userPersonalData } = useUserStore((state) => state)
    const { isTempUser } = useWrapperStore((state) => state)

    const [cookies] = useCookies()

    const context = useWebPushNotificationsContext()
    const { client } = context ?? {}

    const {
        mutate,
        isSuccess,
        reset,
        data: subscribeData,
    } = useSubscribeApiV1WebpushNotificationsSubscribePost()

    const {
        data: activeSubscriptionData,
        error: activeSubscriptionError,
        isPending: isPendingActiveSubscription,
    } = useGetUserListApiV1WebpushNotificationsListGet()

    const userUuid = userPersonalData?.user_uuid
    const isRequestedWebPushPermissionDailyKey = `isRequestedWebPushPermissionDaily-${userUuid}`
    const isRequestedWebPushPermissionMonthlyKey = `isRequestedWebPushPermissionMonthly-${userUuid}`

    useEffect(() => {
        if (
            !isPendingActiveSubscription &&
            !isTempUser &&
            isBrowserSupportedNotifications() &&
            userUuid &&
            !subscribeData
        ) {
            const canShowDialog =
                isUndefined(cookies[isRequestedWebPushPermissionDailyKey]) &&
                isUndefined(cookies[isRequestedWebPushPermissionMonthlyKey])

            switch (Notification.permission) {
                case 'denied': {
                    if (canShowDialog) {
                        onPermissionDenied()
                    }
                    break
                }
                case 'granted': {
                    client?.subscribe((subscription) => {
                        const { endpoint, keys } = subscription.toJSON()

                        if (
                            activeSubscriptionData &&
                            endpoint &&
                            keys &&
                            'auth' in keys &&
                            'p256dh' in keys
                        ) {
                            const isDeviceSubscribed = activeSubscriptionData.some(
                                (subscription) =>
                                    subscription.endpoint === endpoint &&
                                    subscription.keys.auth === keys.auth &&
                                    subscription.keys.p256dh === keys.p256dh,
                            )

                            if (!isDeviceSubscribed && canShowDialog) {
                                // проверка наличия активной подписки на текущем устройстве
                                onRequestPermission()
                            }
                        } else if (canShowDialog) {
                            // запрос подписки на новом устройстве с разрешенными уведомлениями
                            onRequestPermission()
                        }
                    })
                    break
                }
                default: {
                    if (canShowDialog) {
                        onRequestPermission()
                    }
                }
            }
        }
    }, [
        activeSubscriptionData,
        activeSubscriptionError,
        isPendingActiveSubscription,
        isTempUser,
        context,
        userUuid,
    ])

    const subscribe = useCallback(() => {
        client?.subscribe((subscription) => {
            const { endpoint, keys } = subscription.toJSON()

            if (endpoint && keys && 'auth' in keys && 'p256dh' in keys) {
                mutate({
                    data: {
                        endpoint,
                        keys: {
                            auth: keys['auth'],
                            p256dh: keys['p256dh'],
                        },
                    },
                })
            }
        })
    }, [client])

    return {
        subscribe,
        isSuccess,
        reset,
        isRequestedWebPushPermissionMonthlyKey,
        isRequestedWebPushPermissionDailyKey,
    }
}
